// ---------------------------------------------------------------------------
// Restaurants
// ---------------------------------------------------------------------------

/**
 * Public restaurant columns — everything EXCEPT `email`, which is a login
 * identifier neither the anon role (schema §12d) nor plain authenticated users
 * (§19 — consumer accounts exist now) can read. EVERY read of `restaurants`
 * MUST use this list: `select("*")` touches the revoked column and errors for
 * all roles. Staff surfaces that legitimately need the email go through the
 * `get_venue_email` RPC (§19a) — see `getVenueEmail` in reads.ts.
 * ⚠ Keep in sync with BOTH grants: `... to anon` (§12d/§13/§27) and
 *   `... to authenticated` (§19/§27).
 *
 * BASE = columns that exist on every deployed DB; the §27 reservation columns
 * and the §28 fast-venue flag ride separately so reads can fall back
 * pre-migration (see the sticky flags below).
 */
const RESTAURANT_BASE_COLUMNS =
  "id, name, image_url, gallery_image_urls, tagline, tagline_ro, " +
  "description, description_ro, maps_url, cuisine, cuisine_ro, " +
  "cuisine_slugs, tag_slugs, neighborhood, city, visible, " +
  "pos_enabled, pos_explicit, current_capacity, capacity_seats, " +
  "currently_occupied_tables, opens_at, closes_at, lat, lng, created_at, updated_at";

/** §27 reservation load signal columns (fail-soft until Teo runs the block). */
const RESERVATION_COLUMNS =
  "reservations_enabled, reservations_status, reservations_status_updated_at";

/** §28 fast-venue flag (fail-soft until Teo runs the block). */
const FAST_VENUE_COLUMNS = "is_fast_venue";

/** §35a venue URL slug (fail-soft until Teo runs the block). */
const SLUG_COLUMNS = "slug";

/**
 * The public column boundary for `restaurants` reads (`select("*")` errors for
 * every role since §19). A new public column goes here AND in the §19 grants.
 * @public
 */
export const RESTAURANT_PUBLIC_COLUMNS = `${RESTAURANT_BASE_COLUMNS}, ${RESERVATION_COLUMNS}, ${FAST_VENUE_COLUMNS}, ${SLUG_COLUMNS}`;

// Sticky per-session flags: the app deploys before Teo pastes a new §-block,
// so the first restaurants read on a pre-migration DB errors with 42703
// (unknown column on select) or PGRST204 (unknown column in a write payload).
// We drop the newest column group first (§35a slug, the realistic gap), then §28, then §27,
// and skip the doomed attempts from then on. Cleared only by a reload after
// the migration runs — same accepted behavior as the §26 theme fail-soft in
// authStore.
let reservationColumnsMissing = false;
let fastVenueColumnMissing = false;
let slugColumnMissing = false;

function isMissingColumnError(error: { code?: string } | null | undefined): boolean {
  return error?.code === "42703" || error?.code === "PGRST204";
}

export function restaurantColumns(): string {
  let cols = RESTAURANT_BASE_COLUMNS;
  if (!reservationColumnsMissing) cols += `, ${RESERVATION_COLUMNS}`;
  if (!fastVenueColumnMissing) cols += `, ${FAST_VENUE_COLUMNS}`;
  if (!slugColumnMissing) cols += `, ${SLUG_COLUMNS}`;
  return cols;
}

/**
 * On a missing-column error, mark the next-newest column group as absent and
 * report whether a retry (with `restaurantColumns()` recomputed) makes sense.
 * False = the error isn't a missing column, or we're already down to BASE.
 */
export function advanceMissingColumnFlags(error: { code?: string } | null | undefined): boolean {
  if (!isMissingColumnError(error)) return false;
  if (!slugColumnMissing) {
    slugColumnMissing = true;
    return true;
  }
  if (!fastVenueColumnMissing) {
    fastVenueColumnMissing = true;
    return true;
  }
  if (!reservationColumnsMissing) {
    reservationColumnsMissing = true;
    return true;
  }
  return false;
}

/** Drop whichever §27/§28/§35a fields the session has flagged missing from a write payload. */
export function stripMissingColumnFields<T extends object>(input: T): T {
  if (!reservationColumnsMissing && !fastVenueColumnMissing && !slugColumnMissing) return input;
  const rest = { ...(input as Record<string, unknown>) };
  if (reservationColumnsMissing) {
    delete rest.reservations_enabled;
    delete rest.reservations_status;
    delete rest.reservations_status_updated_at;
  }
  if (fastVenueColumnMissing) {
    delete rest.is_fast_venue;
  }
  if (slugColumnMissing) {
    delete rest.slug;
  }
  return rest as T;
}
